import { useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { LockKeyhole, LogOut, RefreshCw, Package, Download, ArrowLeft, Printer } from "lucide-react";
import { getAdminMe, adminLogout, startAdminLogin } from "@/lib/adminAuth";
import {
  fetchAdminPhysicalOrders, fetchAdminPhysicalOrdersSummary, fetchAdminPhysicalOrder,
  updateAdminPhysicalOrderStatus, adminPhysicalOrdersCsvUrl,
} from "@/lib/adminPhysicalOrders";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const STATUSES = ["PAYMENT_PENDING", "PAYMENT_FAILED", "PAID", "PREPARING", "PACKED", "SHIPPED", "DELIVERED", "CANCELLED", "REFUND_PENDING", "REFUNDED"];
const STATUS_COLORS = {
  PAYMENT_PENDING: "text-white/50", PAYMENT_FAILED: "text-red-400", PAID: "text-[#F57F17]",
  PREPARING: "text-amber-300", PACKED: "text-amber-300", SHIPPED: "text-sky-300",
  DELIVERED: "text-emerald-400", CANCELLED: "text-white/40", REFUND_PENDING: "text-red-300", REFUNDED: "text-red-300",
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}
function money(paise) { return `₹${Math.round((paise || 0) / 100)}`; }

function OrderDetail({ orderRef, onUpdated }) {
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({ status: "", courier: "", tracking_number: "", tracking_url: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const o = await fetchAdminPhysicalOrder(orderRef);
    setOrder(o);
    setForm({ status: o.status, courier: o.fulfilment?.courier || "", tracking_number: o.fulfilment?.tracking_number || "", tracking_url: o.fulfilment?.tracking_url || "" });
  }, [orderRef]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const updated = await updateAdminPhysicalOrderStatus(orderRef, form);
      setOrder(updated);
      onUpdated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!order) return <div className="p-8 text-white/50 text-sm">Loading…</div>;
  const d = order.delivery;

  return (
    <div data-testid="physical-order-detail">
      {/* On-screen admin detail (hidden on print) */}
      <div className="print:hidden">
      <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-serif text-xl">{order.order_ref}</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Customer</p>
            <p className="text-white/85">{d.full_name}</p>
            <p className="text-white/60">{d.mobile}{d.alternate_phone ? ` / ${d.alternate_phone}` : ""}</p>
            <p className="text-white/60">{d.email}</p>
          </div>
          <div>
            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Product</p>
            <p className="text-white/85">{order.product_title}</p>
            <p className="text-white/60">Qty {order.quantity} · {money(order.unit_amount)} each · Total {money(order.total_amount)}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Delivery Address</p>
            <p className="text-white/85">{d.address_line1}, {d.address_line2}{d.landmark ? `, ${d.landmark}` : ""}</p>
            <p className="text-white/85">{d.city}{d.district ? `, ${d.district}` : ""}, {d.state} {d.pin_code}, {d.country}</p>
            {d.order_notes && <p className="text-white/50 italic mt-1">Note: {d.order_notes}</p>}
          </div>
          <div>
            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Payment</p>
            <p className="text-white/85">{order.razorpay_payment_id || "—"}</p>
            <p className="text-white/60">{formatDate(order.created_at)}</p>
          </div>
          <div>
            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Source</p>
            <p className="text-white/60">{order.source_page || "—"}</p>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-white/10">
          <p className="text-white/40 text-xs uppercase tracking-wide mb-3">Fulfilment</p>
          {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="physical-order-status-select" className="bg-white/5 border-white/15 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="physical-order-courier-input" placeholder="Courier" value={form.courier} onChange={(e) => setForm((f) => ({ ...f, courier: e.target.value }))} className="bg-white/5 border-white/15 text-white" />
            <Input data-testid="physical-order-tracking-input" placeholder="Tracking / AWB Number" value={form.tracking_number} onChange={(e) => setForm((f) => ({ ...f, tracking_number: e.target.value }))} className="bg-white/5 border-white/15 text-white" />
            <Input data-testid="physical-order-tracking-url-input" placeholder="Tracking URL (optional)" value={form.tracking_url} onChange={(e) => setForm((f) => ({ ...f, tracking_url: e.target.value }))} className="bg-white/5 border-white/15 text-white" />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button type="button" data-testid="physical-order-save-status-btn" onClick={save} disabled={saving} className="px-5 py-2.5 rounded-sm bg-[#F57F17] text-white text-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : "Save Fulfilment Update"}
            </button>
            <button type="button" data-testid="physical-order-print-label-btn" onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-sm border border-white/15 text-white/70 text-sm">
              <Printer size={14} /> Print Address Label
            </button>
          </div>
        </div>

        {order.status_history?.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-white/40 text-xs uppercase tracking-wide mb-2">Status History</p>
            <ul className="space-y-1 text-xs text-white/50">
              {order.status_history.map((h, i) => (
                <li key={i}>{formatDate(h.at)} — {h.status} ({h.changed_by})</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Packing slip — print-only, hidden on screen */}
      <div className="hidden print:block text-black p-8">
        <p className="text-xs uppercase tracking-widest font-bold mb-4">LifeIQ Publications — Packing Slip</p>
        <p className="text-sm mb-1">Order: {order.order_ref}</p>
        <p className="text-lg font-bold mt-4">{d.full_name}</p>
        <p className="text-base">{d.address_line1}</p>
        <p className="text-base">{d.address_line2}{d.landmark ? `, ${d.landmark}` : ""}</p>
        <p className="text-base">{d.city}{d.district ? `, ${d.district}` : ""}, {d.state} {d.pin_code}</p>
        <p className="text-base">{d.country}</p>
        <p className="text-base mt-2">Phone: {d.mobile}</p>
        <p className="text-base mt-4">{order.product_title} × {order.quantity}</p>
      </div>
    </div>
  );
}

/** Owner-only Chinnu physical fulfilment admin — Command 04 §25. Reuses
 * the existing Google-login admin session (same as /admin orders). */
export default function AdminPhysicalOrders() {
  const location = useLocation();
  const [authChecking, setAuthChecking] = useState(location.state?.user ? false : true);
  const [user, setUser] = useState(location.state?.user || null);

  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRef, setSelectedRef] = useState(null);

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      const me = await getAdminMe();
      setUser(me);
      setAuthChecking(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, summaryRes] = await Promise.all([
        fetchAdminPhysicalOrders({ status: statusFilter === "all" ? undefined : statusFilter, search: search || undefined, limit: 200 }),
        fetchAdminPhysicalOrdersSummary(),
      ]);
      setOrders(ordersRes.orders);
      setSummary(summaryRes);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const handleLogout = async () => { await adminLogout(); setUser(null); };

  if (authChecking) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0D1B2A]"><p className="text-white/50 text-sm tracking-[0.14em] uppercase">Checking access…</p></div>;
  }

  if (!user) {
    return (
      <div data-testid="admin-physical-orders-login-gate" className="min-h-screen flex items-center justify-center bg-[#0D1B2A] px-6">
        <div className="max-w-sm w-full text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-6"><LockKeyhole size={22} className="text-[#F57F17]" /></div>
          <h1 className="text-white font-serif text-2xl mb-2">LifeIQ Admin</h1>
          <p className="text-white/50 text-sm mb-8">Sign in with the owner Google account to manage Chinnu physical orders.</p>
          <button type="button" data-testid="admin-physical-orders-google-login-btn" onClick={startAdminLogin} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[13px] font-semibold tracking-wide uppercase rounded-sm bg-[#F57F17] text-white hover:bg-[#D86B0F] transition-colors">
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-physical-orders-page" className="min-h-screen bg-[#0D1B2A] px-6 py-10 print:bg-white print:p-0">
      <div className="max-w-6xl mx-auto print:hidden">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-white/40 hover:text-white/70 text-[11px] uppercase tracking-wide transition-colors mb-2"><ArrowLeft size={12} /> Order History</Link>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#F57F17] font-semibold mb-1">LifeIQ Admin</p>
            <h1 className="text-white font-serif text-2xl">Chinnu Printed-Book Orders</h1>
          </div>
          <div className="flex items-center gap-4">
            <span data-testid="admin-physical-orders-user-email" className="text-white/50 text-sm">{user.email}</span>
            <button type="button" onClick={handleLogout} className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[12px] uppercase tracking-wide transition-colors"><LogOut size={14} /> Log out</button>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            {STATUSES.map((s) => (
              <div key={s} data-testid={`physical-order-count-${s.toLowerCase()}`} className="rounded-sm border border-white/10 bg-white/[0.03] p-3">
                <p className="text-white/40 text-[10px] uppercase tracking-wide">{s.replace("_", " ")}</p>
                <p className={`text-xl font-serif mt-1 ${STATUS_COLORS[s]}`}>{summary.counts[s] || 0}</p>
              </div>
            ))}
            <div data-testid="physical-order-total-revenue" className="rounded-sm border border-[#F57F17]/30 bg-[#F57F17]/10 p-3">
              <p className="text-[#F57F17] text-[10px] uppercase tracking-wide font-semibold">Total Revenue</p>
              <p className="text-xl font-serif mt-1 text-white">{money(summary.total_revenue)}</p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="physical-order-filter-status" className="w-48 bg-white/5 border-white/15 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="physical-order-search-input" placeholder="Search name, mobile, email, order ID…" value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 min-w-[220px] bg-white/5 border-white/15 text-white" />
          <button type="button" onClick={load} className="px-3 py-2 rounded-sm border border-white/15 text-white/60 hover:text-white"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
          <a href={adminPhysicalOrdersCsvUrl()} data-testid="physical-orders-csv-export" className="inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold uppercase tracking-wide rounded-sm border border-white/15 text-white/70 hover:text-white"><Download size={13} /> Export CSV</a>
        </div>

        <div className="rounded-sm border border-white/10 bg-white/[0.03] overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/50">Order ID</TableHead>
                <TableHead className="text-white/50">Date</TableHead>
                <TableHead className="text-white/50">Customer</TableHead>
                <TableHead className="text-white/50">Product</TableHead>
                <TableHead className="text-white/50">Amount</TableHead>
                <TableHead className="text-white/50">Status</TableHead>
                <TableHead className="text-white/50">City / PIN</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && !loading && (
                <TableRow className="border-white/10 hover:bg-transparent"><TableCell colSpan={7} className="text-white/40 text-center py-10">No orders yet.</TableCell></TableRow>
              )}
              {orders.map((o) => (
                <TableRow key={o.order_ref} data-testid={`physical-order-row-${o.order_ref}`} className="border-white/10 hover:bg-white/[0.04] cursor-pointer" onClick={() => setSelectedRef(o.order_ref)}>
                  <TableCell className="text-white/80 font-mono text-xs flex items-center gap-1.5"><Package size={12} /> {o.order_ref}</TableCell>
                  <TableCell className="text-white/60 text-xs">{formatDate(o.created_at)}</TableCell>
                  <TableCell className="text-white/70 text-xs">{o.delivery?.full_name}</TableCell>
                  <TableCell className="text-white/60 text-xs">{o.product_title}</TableCell>
                  <TableCell className="text-white/70 text-xs">{money(o.total_amount)}</TableCell>
                  <TableCell className={`text-xs font-semibold ${STATUS_COLORS[o.status]}`}>{o.status}</TableCell>
                  <TableCell className="text-white/50 text-xs">{o.delivery?.city}, {o.delivery?.pin_code}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!selectedRef} onOpenChange={(open) => !open && setSelectedRef(null)}>
        <DialogContent className="bg-[#0D1B2A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto print:bg-white print:max-w-full print:max-h-full">
          {selectedRef && <OrderDetail orderRef={selectedRef} onUpdated={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
